/**
 * Šprtec 3D — počítačový protihráč pro singleplayer.
 * Hledá kámen a směr tak, aby přes „kulečníkový“ ghost bod poslal puk
 * na branku soupeře; podle obtížnosti přidává chybu miření.
 */

import { RINK, PUCK_ID, MAX_SHOT_SPEED, castDisc } from './physics.js';

const L2 = RINK.L / 2;
const GW2 = RINK.GW / 2;

export const AI_LEVELS = {
  easy: { aimNoise: 0.16, powerNoise: 0.3, blunderChance: 0.35 },
  medium: { aimNoise: 0.07, powerNoise: 0.15, blunderChance: 0.12 },
  hard: { aimNoise: 0.02, powerNoise: 0.06, blunderChance: 0.02 },
};

function norm(x, z) {
  const l = Math.hypot(x, z) || 1;
  return { x: x / l, z: z / l };
}

/**
 * Vybere tah pro stranu `side`. Vrací { pieceId, vx, vz } nebo null.
 */
export function chooseShot(game, side, level = 'medium') {
  const cfg = AI_LEVELS[level] ?? AI_LEVELS.medium;
  const bodies = game.bodies;
  const puck = game.body(PUCK_ID);
  const myPieces = bodies.filter((b) => b.kind === 'piece' && b.side === side);
  const goalZ = side === 0 ? L2 : -L2;
  const ownGoalZ = -goalZ;

  // cílové body v brance soupeře (střed + tyčky)
  const targets = [
    { x: 0, z: goalZ + 0.05 },
    { x: -(GW2 - 0.08), z: goalZ + 0.05 },
    { x: GW2 - 0.08, z: goalZ + 0.05 },
  ];

  let best = null;

  for (const piece of myPieces) {
    for (const target of targets) {
      const toGoal = norm(target.x - puck.x, target.z - puck.z);
      // ghost bod: kam musí dorazit střed kamene, aby puk letěl na cíl
      const ghost = {
        x: puck.x - toGoal.x * (piece.r + puck.r),
        z: puck.z - toGoal.z * (piece.r + puck.r),
      };
      const dx = ghost.x - piece.x;
      const dz = ghost.z - piece.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 0.01) continue;
      const dir = norm(dx, dz);

      // kámen musí do puku strčit zhruba směrem na branku
      const align = dir.x * toGoal.x + dir.z * toGoal.z;
      if (align < 0.2) continue;

      // cesta kamene ke ghost bodu musí být volná (kromě puku samotného)
      const cast = castDisc(
        piece.x, piece.z, dir.x, dir.z, piece.r, bodies,
        [piece.id, PUCK_ID], dist,
      );
      if (cast.hit) continue;

      // volná dráha puku na branku zvyšuje skóre kandidáta
      const puckPath = castDisc(
        puck.x, puck.z, toGoal.x, toGoal.z, puck.r, bodies,
        [PUCK_ID, piece.id],
        Math.hypot(target.x - puck.x, target.z - puck.z) - 0.05,
      );
      const clearBonus = puckPath.hit ? -0.8 : 0.6;

      const score = align * 2 + clearBonus - dist * 0.25 + Math.random() * 0.15;
      if (!best || score > best.score) {
        best = { piece, dir, dist, align, score };
      }
    }
  }

  const blunder = Math.random() < cfg.blunderChance;

  if (!best || blunder) {
    // nouzový/zkažený tah: nejbližší kámen prostě plácne do puku
    let nearest = null;
    for (const piece of myPieces) {
      const d = Math.hypot(puck.x - piece.x, puck.z - piece.z);
      if (!nearest || d < nearest.d) nearest = { piece, d };
    }
    if (!nearest) return null;
    const dir = norm(puck.x - nearest.piece.x, puck.z - nearest.piece.z);
    const angle = Math.atan2(dir.z, dir.x) + (Math.random() - 0.5) * 2 * (cfg.aimNoise + 0.1);
    const speed = Math.min(MAX_SHOT_SPEED, 2 + nearest.d * 1.6 + Math.random());
    return {
      pieceId: nearest.piece.id,
      vx: Math.cos(angle) * speed,
      vz: Math.sin(angle) * speed,
    };
  }

  // obranná pojistka: pokud je puk nebezpečně u vlastní branky, radši odpal
  const puckDangerous = Math.abs(puck.z - ownGoalZ) < 0.7 && Math.abs(puck.x) < 0.5;
  if (puckDangerous && best.align < 0.55) {
    const away = norm(puck.x * 0.4, goalZ - puck.z);
    const ghostDir = norm(
      puck.x - away.x * 0.135 - best.piece.x,
      puck.z - away.z * 0.135 - best.piece.z,
    );
    best = { ...best, dir: ghostDir };
  }

  const angle = Math.atan2(best.dir.z, best.dir.x)
    + (Math.random() - 0.5) * 2 * cfg.aimNoise;
  let speed = 2.4 + best.dist * 1.3 + best.align * 1.6;
  speed *= 1 + (Math.random() - 0.5) * 2 * cfg.powerNoise;
  speed = Math.max(1.4, Math.min(MAX_SHOT_SPEED, speed));

  return {
    pieceId: best.piece.id,
    vx: Math.cos(angle) * speed,
    vz: Math.sin(angle) * speed,
  };
}
